import axios from "axios";
import CarbonClient from "../CarbonClient";

// for speaking to speckle directly;
// the imported CarbonClient prefixes routes with /rest
const SpeckleClient = axios.create({
  baseURL: import.meta.env.VITE_SPECKLE_URL,
  timeout: 0,
});

//PUT

//GET

export type SpeckleUserStatus = "linked" | "unlinked" | "none";

export interface SpeckleUserStatusResponse {
  status: SpeckleUserStatus;
  email: string | null;
  speckleId: string | null;
}

// Where the calling user stands with Speckle: linked = speckleUserData row exists,
// unlinked = a Speckle account matches their email but isn't linked yet, none = neither.
export const getSpeckleUserStatus = async (): Promise<SpeckleUserStatusResponse> => {
  const response = await CarbonClient.get<SpeckleUserStatusResponse>("api/Speckle/user/status");
  return response.data;
};

//POST

export interface SpeckleProjectAssignment {
  assigned: number;
  total: number;
}

// Idempotent catch-up sync: invites the linked Speckle account to every accessible project
// stream it isn't a member of yet. Safe to call repeatedly — already-member streams count as
// assigned. 404 = the user has no linked Speckle account.
export const assignSpeckleUserToProjects = async (): Promise<SpeckleProjectAssignment> => {
  const response = await CarbonClient.post<SpeckleProjectAssignment>("api/Speckle/user/assign-projects", "");
  return response.data;
};

// Create a Speckle account for the calling user with the password they chose, and link it in
// speckleUserData. Identity comes from the bearer token. 400 = password rejected, 409 = an
// account or mapping row already exists.
export const createSpeckleUser = async (password: string): Promise<void> => {
  await CarbonClient.post("api/Speckle/user", { password });
};

// Finish the Speckle authorize flow: the backend exchanges the access code + challenge for a
// token (using the registered "Real-Time LCA" app) and links the account in speckleUserData.
// 409 = the consenting Speckle account belongs to someone else, or is already linked.
export const connectSpeckleUser = async (accessCode: string, challenge: string): Promise<void> => {
  await CarbonClient.post("api/Speckle/user/connect", { accessCode, challenge });
};

// Speckle's public forgot-password endpoint — unauthenticated by design. The server answers
// 200 even for unknown emails (anti-enumeration), so success only means "request accepted",
// not "account exists". Throws (axios default) on network/4xx+ errors.
export const requestSpecklePasswordReset = async (email: string): Promise<void> => {
  await SpeckleClient.post("/auth/pwdreset/request", { email });
};

export const createStreamAndBranches = async (streamName: string | null, defaultBranches: Array<number>) => {
  const requestBody = {
    streamName,
    branches: defaultBranches,
  };

  const response = await CarbonClient.post("api/Speckle/streamDefaultBranches", requestBody);

  if (response.status === 200 || response.status === 201) {
    return response.data;
  } else {
    return response.status;
  }
};

export const createNewBranchForStream = async (request: {
  streamId: string;
  branches: Array<{ name: string; description: string }>;
}) => {
  const response = await CarbonClient.post("api/Speckle/branch", request);
  if (response.status === 200) {
    return response.data;
  } else {
    return response.status;
  }
};

// returns false on error
export const uploadIFCModel = async (request: { streamId: string; modelName: string; file: Blob }): Promise<boolean> => {
  const url = `/api/file/autodetect/${request.streamId}/${request.modelName}`;
  const data = new FormData();
  data.append("file", request.file);

  const token = await getSpeckleToken();

  if (!token) {
    return false;
  }

  const response = await SpeckleClient.post(url, data, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status > 400) {
    return false;
  }

  // This starts the 'monitoring' process in the backend
  // After this we can poll Hasura to wait until the upload is done
  // TODO rename the endpoint in the backend!
  await CarbonClient.post(`api/Speckle/start-upload/${request.streamId}`, "");

  return true;
};

//DELETE
export const deleteSpeckleStream = async ({ streamId }: { streamId: string }): Promise<boolean> => {
  const response = await CarbonClient.delete(`/api/Speckle/stream/${streamId}`);

  return response.status === 200;
};

let cachedToken: string | null = null;
let tokenPromise: Promise<string | undefined> | null = null;

// Refresh slightly before the JWT actually expires so an in-flight request can't
// land on a just-expired token. Anything under ~10s is risky once you account for
// clock skew + network latency on the next fetch.
const TOKEN_EXPIRY_BUFFER_MS = 30_000;

/**
 * Decode the `exp` claim from a JWT without verifying the signature.
 * Returns `true` for any token we can't decode — caller treats it as expired,
 * which means we'll refresh. Safer to over-fetch than to keep using a bad token.
 */
const isTokenExpired = (token: string): boolean => {
  try {
    const [, payloadB64] = token.split(".");
    if (!payloadB64) return true;
    // base64url → base64 + padding so atob accepts it
    const padded = payloadB64.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(padded + "===".slice(0, (4 - (padded.length % 4)) % 4)));
    if (typeof payload.exp !== "number") return false; // no `exp` claim → trust forever
    return payload.exp * 1000 - TOKEN_EXPIRY_BUFFER_MS < Date.now();
  } catch {
    return true;
  }
};

export const getSpeckleToken = async (): Promise<string | undefined> => {
  // Reuse the cached token only if it's still valid — JWTs have a finite TTL
  // and the previous "cache forever" behavior caused 401s after expiry.
  if (cachedToken && !isTokenExpired(cachedToken)) {
    return cachedToken;
  }

  // Return existing promise if a fetch is already in progress
  if (tokenPromise) {
    return tokenPromise;
  }

  tokenPromise = (async () => {
    try {
      const response = await CarbonClient.get("api/Speckle/access-token");
      if (response.status === 200) {
        cachedToken = response.data;
        return response.data;
      }
      return undefined;
    } catch (error) {
      console.error("Failed to fetch Speckle token:", error);
      return undefined;
    } finally {
      tokenPromise = null;
    }
  })();

  return tokenPromise;
};
